'use client';

import { useState, useMemo } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import NextLink from 'next/link';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Link as MuiLink,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import { BotyLogo } from '@boty/ui';
import { useLocale, useTranslations } from 'next-intl';
import { createBrowserClient } from '@supabase/ssr';
import { usePermissions } from '@/features/team/usePermissions';
import type { Permission } from '@/lib/permissions';

interface NavItem {
  label: string;
  href: string;
  match: string; // pathname prefix to match for active state
  requiredPermission?: Permission; // Optional permission required to see this nav item
}

export function AdminHeader() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations('admin');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const { can } = usePermissions();

  const allNavItems: NavItem[] = [
    {
      label: t('nav.dashboard'),
      href: `/${locale}/dashboard`,
      match: '/dashboard',
    },
    {
      label: t('nav.users'),
      href: `/${locale}/users`,
      match: '/users',
      requiredPermission: 'users.view',
    },
    {
      label: t('nav.bots'),
      href: `/${locale}/bots`,
      match: '/bots',
      requiredPermission: 'bots.view',
    },
    {
      label: t('nav.costs'),
      href: `/${locale}/costs`,
      match: '/costs',
      requiredPermission: 'costs.view',
    },
    {
      label: t('nav.analytics'),
      href: `/${locale}/analytics`,
      match: '/analytics',
      requiredPermission: 'analytics.view',
    },
    {
      label: t('nav.auditLog'),
      href: `/${locale}/audit-log`,
      match: '/audit-log',
      requiredPermission: 'audit.view',
    },
    {
      label: t('nav.team'),
      href: `/${locale}/team`,
      match: '/team',
      requiredPermission: 'team.view',
    },
    {
      label: t('nav.cronJobs'),
      href: `/${locale}/cron-jobs`,
      match: '/cron-jobs',
    },
    {
      label: t('nav.feedback'),
      href: `/${locale}/feedback`,
      match: '/feedback',
    },
    {
      label: t('nav.blog'),
      href: `/${locale}/blog`,
      match: '/blog',
    },
    {
      label: t('nav.settings'),
      href: `/${locale}/settings/security`,
      match: '/settings',
    },
  ];

  // Filter nav items based on permissions
  const navItems = useMemo(() => {
    return allNavItems.filter((item) => {
      if (!item.requiredPermission) return true;
      return can(item.requiredPermission);
    });
  }, [can]);

  const strippedPath = pathname?.replace(/^\/(en|he)/, '') || '/';

  const isActive = (match: string) => strippedPath.startsWith(match);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavClick = (href: string) => {
    handleMenuClose();
    router.push(href);
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      const supabase = createBrowserClient(
        process.env.NEXT_PUBLIC_SUPABASE_URL!,
        process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
      );
      await supabase.auth.signOut();

      // Clear the 2FA cookie by calling a logout endpoint or letting middleware handle it
      // The signOut clears auth cookies; we also need to clear admin_2fa_verified
      document.cookie = 'admin_2fa_verified=; path=/; max-age=0';

      router.push(`/${locale}/login`);
    } catch {
      router.push(`/${locale}/login`);
    }
  };

  return (
    <AppBar
      position="static"
      color="transparent"
      elevation={0}
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Logo */}
        <Box
          onClick={() => router.push(`/${locale}/dashboard`)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
        >
          <BotyLogo size="small" />
        </Box>

        {/* Right side: nav + logout */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 3, mr: 2 }}>
              {navItems.map((item) => (
                <MuiLink
                  key={item.href}
                  component={NextLink}
                  href={item.href}
                  underline="none"
                  sx={{
                    color: isActive(item.match) ? 'primary.main' : 'text.primary',
                    fontWeight: isActive(item.match) ? 600 : 500,
                    borderBottom: isActive(item.match) ? 2 : 0,
                    borderColor: 'primary.main',
                    pb: 0.5,
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  {item.label}
                </MuiLink>
              ))}
            </Box>
          )}

          {/* Logout button (desktop) */}
          {!isMobile && (
            <Button
              onClick={handleLogout}
              disabled={loggingOut}
              startIcon={<LogoutIcon />}
              size="small"
              color="inherit"
              sx={{ color: 'text.secondary' }}
            >
              {t('nav.logout')}
            </Button>
          )}

          {/* Mobile Hamburger Menu */}
          {isMobile && (
            <>
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleMenuOpen}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {navItems.map((item) => (
                  <MenuItem
                    key={item.href}
                    onClick={() => handleNavClick(item.href)}
                    selected={isActive(item.match)}
                  >
                    {item.label}
                  </MenuItem>
                ))}
                <MenuItem
                  onClick={handleLogout}
                  disabled={loggingOut}
                  sx={{ color: 'error.main' }}
                >
                  {t('nav.logout')}
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}
